import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Phone, PhoneCall, PhoneOff, Loader2 } from 'lucide-react';

/**
 * Click-to-Call Component
 * 
 * Initiates a call through the configured telephony provider.
 * Falls back to tel: link if no telephony is configured.
 * 
 * Props:
 * - phone: Phone number to call (required)
 * - leadId: Optional lead ID for logging
 * - leadName: Optional lead name for display
 * - variant: 'icon' | 'button' | 'inline' (default: 'icon')
 * - size: 'sm' | 'md' | 'lg' (default: 'md')
 * - className: Additional CSS classes
 * - onCallInitiated: Callback when call starts
 * - onCallEnded: Callback when call ends
 */
export function ClickToCall({ 
  phone, 
  leadId, 
  leadName,
  variant = 'icon',
  size = 'md',
  className = '',
  onCallInitiated,
  onCallEnded 
}) {
  const [calling, setCalling] = useState(false);
  const [callStatus, setCallStatus] = useState(null); // 'initiating' | 'ringing' | 'connected' | 'ended' | 'failed'
  const [error, setError] = useState(null);

  // Don't render if no phone number
  if (!phone || phone === '-') {
    return null;
  }

  // Format phone number for display
  const formatPhone = (num) => {
    if (!num) return '';
    // Remove non-digits except + at start
    const cleaned = num.replace(/[^\d+]/g, '');
    return cleaned;
  };

  const handleCall = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setError(null);
    setCalling(true);
    setCallStatus('initiating');

    try {
      // Try to initiate call through telephony API
      const response = await axios.post(`${API}/calls/initiate`, {
        phone: formatPhone(phone),
        lead_id: leadId || null
      });

      setCallStatus('ringing');
      onCallInitiated?.(response.data);

      // Poll for call status or handle via websocket in future
      // For now, assume call will complete
      setTimeout(() => {
        setCallStatus('connected');
      }, 2000);

    } catch (err) {
      console.error('Call initiation error:', err);
      
      // If telephony not configured, fall back to tel: link
      if (err.response?.status === 400 || err.response?.status === 404) {
        // Fallback to native tel: link
        window.location.href = `tel:${formatPhone(phone)}`;
        setCallStatus(null);
        setCalling(false);
        return;
      }

      setError(err.response?.data?.detail || 'Failed to initiate call');
      setCallStatus('failed');
    } finally {
      // Reset after delay
      setTimeout(() => {
        setCalling(false);
        setCallStatus(null);
        setError(null);
      }, 3000);
    }
  };

  // Size classes
  const sizeClasses = {
    sm: 'w-7 h-7 text-xs',
    md: 'w-9 h-9 text-sm',
    lg: 'w-11 h-11 text-base'
  };

  const iconSizes = {
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  // Status colors
  const getStatusColor = () => {
    switch (callStatus) {
      case 'initiating':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800';
      case 'ringing':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 animate-pulse';
      case 'connected':
        return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800';
      case 'failed':
        return 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800';
      default:
        return 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 hover:border-emerald-300 dark:hover:border-emerald-700';
    }
  };

  // Icon variant (small circle button)
  if (variant === 'icon') {
    return (
      <button
        onClick={handleCall}
        disabled={calling}
        className={`${sizeClasses[size]} rounded-full border flex items-center justify-center transition-all duration-200 ${getStatusColor()} ${className}`}
        title={calling ? `Calling ${leadName || phone}...` : `Call ${leadName || phone}`}
        data-testid="click-to-call-icon"
      >
        {calling ? (
          callStatus === 'failed' ? (
            <PhoneOff className={iconSizes[size]} />
          ) : callStatus === 'connected' ? (
            <PhoneCall className={iconSizes[size]} />
          ) : (
            <Loader2 className={`${iconSizes[size]} animate-spin`} />
          )
        ) : (
          <Phone className={iconSizes[size]} />
        )}
      </button>
    );
  }

  // Button variant (full button with text)
  if (variant === 'button') {
    return (
      <button
        onClick={handleCall}
        disabled={calling}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
          calling 
            ? getStatusColor() 
            : 'bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800'
        } ${className}`}
        data-testid="click-to-call-btn"
      >
        {calling ? (
          <>
            {callStatus === 'failed' ? (
              <PhoneOff className="w-4 h-4" />
            ) : callStatus === 'connected' ? (
              <PhoneCall className="w-4 h-4" />
            ) : (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
            <span>
              {callStatus === 'initiating' && 'Initiating...'}
              {callStatus === 'ringing' && 'Ringing...'}
              {callStatus === 'connected' && 'Connected'}
              {callStatus === 'failed' && 'Failed'}
            </span>
          </>
        ) : (
          <>
            <Phone className="w-4 h-4" />
            <span>Call</span>
          </>
        )}
      </button>
    );
  }

  // Inline variant (clickable phone number)
  if (variant === 'inline') {
    return (
      <button
        onClick={handleCall}
        disabled={calling}
        className={`inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors group ${className}`}
        title={`Call ${phone}`}
        data-testid="click-to-call-inline"
      >
        <Phone className={`${iconSizes[size]} ${calling ? 'text-emerald-500 dark:text-emerald-400 animate-pulse' : 'text-slate-400 dark:text-slate-500 group-hover:text-emerald-500 dark:group-hover:text-emerald-400'}`} />
        <span className="font-mono text-sm">{phone}</span>
      </button>
    );
  }

  return null;
}

/**
 * Phone Field with Click-to-Call
 * 
 * A phone display field that includes a call button.
 * Used in lead detail pages and contact cards.
 */
export function PhoneFieldWithCall({ 
  phone, 
  leadId, 
  leadName,
  label = 'Phone',
  className = '' 
}) {
  if (!phone || phone === '-') {
    return (
      <div className={className}>
        <label className="form-label mb-2">{label}</label>
        <p className="text-slate-500 dark:text-slate-400">-</p>
      </div>
    );
  }

  return (
    <div className={className}>
      <label className="form-label mb-2">{label}</label>
      <div className="flex items-center gap-2">
        <p className="text-slate-900 dark:text-white font-mono">{phone}</p>
        <ClickToCall 
          phone={phone} 
          leadId={leadId} 
          leadName={leadName}
          variant="icon"
          size="sm"
        />
      </div>
    </div>
  );
}

export default ClickToCall;
